import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import type {
  RegistryCheck,
  RegistryFeatureConfig,
  UpdateCheckPayload,
  UpdateFeatureConfigPayload,
  CreateFeatureConfigPayload,
} from "@/types/validator-registry";

export const CHECKS_KEY = ["checks"] as const;

export const useGetChecks = () =>
  useQuery<RegistryCheck[]>({
    queryKey: CHECKS_KEY,
    queryFn: () => apiService.get<RegistryCheck[], undefined>("/checks"),
    staleTime: DEFAULT_STALE_TIME,
  });

// AUT-401: the switches (check master + per-prompt "Enabled for this prompt") felt unresponsive
// because the flip only landed after the PATCH resolved + a full /checks refetch (~2-3s). Apply the
// change to the cache in `onMutate` so the toggle reflects instantly, roll back on error, and still
// reconcile against the server in `onSettled`.
type ChecksSnapshot = { previous?: RegistryCheck[] };

export const useUpdateCheck = (id: string) =>
  useMutation<RegistryCheck, unknown, UpdateCheckPayload, ChecksSnapshot>({
    mutationFn: (payload) =>
      apiService.patch<RegistryCheck, UpdateCheckPayload>(`/checks/${id}`, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: CHECKS_KEY });
      const previous = queryClient.getQueryData<RegistryCheck[]>(CHECKS_KEY);
      queryClient.setQueryData<RegistryCheck[]>(CHECKS_KEY, (old) =>
        old?.map((c) => (c.id === id ? { ...c, ...payload } : c))
      );
      return { previous };
    },
    onError: (error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(CHECKS_KEY, context.previous);
      handleApiError(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
    },
  });

export const useUpdateFeatureCheckConfig = (featureName: string, checkId: string) =>
  useMutation<RegistryFeatureConfig, unknown, UpdateFeatureConfigPayload, ChecksSnapshot>({
    mutationFn: (payload) =>
      apiService.patch<RegistryFeatureConfig, UpdateFeatureConfigPayload>(
        `/feature-check-config/${featureName}/${checkId}`,
        payload
      ),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: CHECKS_KEY });
      const previous = queryClient.getQueryData<RegistryCheck[]>(CHECKS_KEY);
      queryClient.setQueryData<RegistryCheck[]>(CHECKS_KEY, (old) =>
        old?.map((c) =>
          c.id !== checkId
            ? c
            : {
                ...c,
                feature_configs: c.feature_configs.map((fc) =>
                  fc.feature_name === featureName ? { ...fc, ...payload } : fc
                ),
              }
        )
      );
      return { previous };
    },
    onError: (error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(CHECKS_KEY, context.previous);
      handleApiError(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
    },
  });

export const useCreateFeatureCheckConfig = () =>
  useMutation<RegistryFeatureConfig, unknown, CreateFeatureConfigPayload>({
    mutationFn: (payload) =>
      apiService.post<RegistryFeatureConfig, CreateFeatureConfigPayload>(
        "/feature-check-config",
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
    },
    onError: (error) => handleApiError(error),
  });

export const useDeleteFeatureCheckConfig = (featureName: string, checkId: string) =>
  useMutation<unknown, unknown, void>({
    mutationFn: () =>
      apiService.delete(`/feature-check-config/${featureName}/${checkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
    },
    onError: (error) => handleApiError(error),
  });
